import { Tooltip } from '../../../../Tooltip';
import { SpeakerIcon } from '../../../../Icons';
import { AlbumActionsWrapper } from '../../../../Actions/AlbumActions';
import { ArtistActionsWrapper } from '../../../../Actions/ArtistActions';
import { PlayistActionsWrapper } from '../../../../Actions/PlaylistActions';

// Services
import { playerService } from '../../../../../services/player';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import {
  MIX_BUILDER_PLAYLIST_ID,
  MIX_BUILDER_ROUTE,
  MIX_BUILDER_URI,
  yourLibraryActions,
} from '../../../../../store/slices/yourLibrary';

// Constants
import { ARTISTS_DEFAULT_IMAGE, PLAYLIST_DEFAULT_IMAGE } from '../../../../../constants/spotify';

// Interface
import type { Album } from '../../../../../interfaces/albums';
import type { Artist } from '../../../../../interfaces/artist';
import type { Playlist } from '../../../../../interfaces/playlists';
import type { LibraryRecording } from '../../../../../interfaces/recording';
import type { LibraryWork } from '../../../../../interfaces/work';
import type { LibraryComposer } from '../../../../../interfaces/composer';
import { memo, type ReactNode } from 'react';
import { uiActions } from '../../../../../store/slices/ui';
import { useComposerIdSet } from '../../../../../utils/useComposerIdSet';
import { useComposerImageMap } from '../../../../../utils/useComposerImageMap';
import { getCatalogIdForSpotifyId } from '../../../../../utils/composerRegistry';
import { highlightText } from './highlight';
import { resolveWorkImageUrl } from './workUtils';
import NavigableLink from '../../../../shared/NavigableLink';
import './mixBuilderIcon.scss';

export interface CardShortProps {
  uri: string;
  image: string;
  title: ReactNode;
  subtitle: ReactNode;
  rounded?: boolean;
  disabled?: boolean;
  disablePlayback?: boolean;
  isCurrent?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  linkTo?: string;
  beforeNavigate?: () => boolean;
}

export const LibraryPlayGlyph = (
  <svg
    data-encore-id='icon'
    role='img'
    width={26}
    height={26}
    fill='white'
    aria-hidden='true'
    className='Svg-sc-ytk21e-0 bneLcE zOsKPnD_9x3KJqQCSmAq'
    viewBox='0 0 24 24'
  >
    <path d='m7.05 3.606 13.49 7.788a.7.7 0 0 1 0 1.212L7.05 20.394A.7.7 0 0 1 6 19.788V4.212a.7.7 0 0 1 1.05-.606z'></path>
  </svg>
);

export const LibraryPauseGlyph = (
  <svg
    data-encore-id='icon'
    role='img'
    width={26}
    height={26}
    fill='white'
    aria-hidden='true'
    className='Svg-sc-ytk21e-0 bneLcE zOsKPnD_9x3KJqQCSmAq'
    viewBox='0 0 24 24'
  >
    <path d='M5.7 3a.7.7 0 0 0-.7.7v16.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V3.7a.7.7 0 0 0-.7-.7H5.7zm10 0a.7.7 0 0 0-.7.7v16.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V3.7a.7.7 0 0 0-.7-.7h-2.6z'></path>
  </svg>
);

export const CollapsedCard = (props: CardShortProps) => {
  const { image, title, subtitle, onClick, linkTo, beforeNavigate, uri } = props;
  const isMixBuilder = uri === MIX_BUILDER_URI;

  return (
    <Tooltip
      placement='right'
      title={
        <div>
          <p>{title}</p>
          <p style={{ fontSize: 13, color: 'gray', fontWeight: 400 }}>{subtitle}</p>
        </div>
      }
    >
      {linkTo ? (
        <NavigableLink
          to={linkTo}
          beforeNavigate={beforeNavigate}
          className='library-card collapsed'
          style={{ borderRadius: 10, display: 'flex', justifyContent: 'center' }}
        >
          <div className={`image h-full items-center ${props.rounded ? 'rounded' : ''}`}>
            <div className='image-container'>
              {isMixBuilder ? (
                <div className={`mix-builder-icon small ${props.rounded ? 'rounded' : ''}`} style={{ width: 52, height: 52 }} />
              ) : (
                <img src={image} alt='' style={{ width: 52, height: 52 }} />
              )}
            </div>
          </div>
        </NavigableLink>
      ) : (
        <button
          onClick={onClick}
          className='library-card collapsed'
          onDoubleClick={props.onDoubleClick}
          style={{ borderRadius: 10, display: 'flex', justifyContent: 'center' }}
        >
          <div className={`image h-full items-center ${props.rounded ? 'rounded' : ''}`}>
            <div className='image-container'>
              {isMixBuilder ? (
                <div className={`mix-builder-icon small ${props.rounded ? 'rounded' : ''}`} style={{ width: 52, height: 52 }} />
              ) : (
                <img src={image} alt='' style={{ width: 52, height: 52 }} />
              )}
            </div>
          </div>
        </button>
      )}
    </Tooltip>
  );
};

const isMixBuilderPlaylist = (playlist?: Playlist | null) =>
  playlist?.id === MIX_BUILDER_PLAYLIST_ID;

const CardList = (props: CardShortProps) => {
  const {
    image,
    title,
    subtitle,
    isCurrent,
    onClick,
    disabled,
    disablePlayback,
    linkTo,
    beforeNavigate,
    uri,
  } = props;
  const isMixBuilder = uri === MIX_BUILDER_URI;

  const isPlaying = useAppSelector((state) => !state.spotify.state?.paused);

  const button = disabled || disablePlayback ? null : (
    <button
      className='image-button'
      onClick={async (e) => {
        if (e && e.stopPropagation) {
          e.stopPropagation();
        }
        if (isCurrent && isPlaying) {
          return playerService.pausePlayback();
        }
        return playerService.startPlayback(!isCurrent ? { context_uri: props.uri } : undefined);
      }}
    >
      {isCurrent && isPlaying ? LibraryPauseGlyph : LibraryPlayGlyph}
    </button>
  );

  const body = (
    <div
      className='library-card'
      style={{ borderRadius: 10 }}
      onDoubleClick={props.onDoubleClick}
    >
      <div className={`image p-2 h-full items-center ${props.rounded ? 'rounded' : ''}`}>
        <div style={{ position: 'relative' }}>
          <div>
            {isMixBuilder ? (
              <div className={`mix-builder-icon small ${props.rounded ? 'rounded' : ''}`} style={{ width: 52, height: 52 }} />
            ) : (
              <img
                src={image}
                alt='song cover'
                className='rounded-md'
                style={{ width: 52, height: 52 }}
              />
            )}
          </div>
          {button}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div id='playlist-song-and-artist-name'>
          <h3
            className='text-md font-semibold text-white'
            style={{
              fontSize: 15,
              marginBottom: -5,
              color: isCurrent ? '#1db954' : undefined,
              fontWeight: 100,
            }}
          >
            {title}
          </h3>

          <p
            className='text-md font-semibold text-white'
            style={{
              fontSize: 13,
              opacity: 0.7,
              fontWeight: 400,
            }}
          >
            {subtitle}
          </p>
        </div>

        <div style={{ padding: 8 }}>
          {isCurrent ? <SpeakerIcon fill='#1db954' height={16} width={16} /> : null}
        </div>
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <NavigableLink to={linkTo} beforeNavigate={beforeNavigate} onClick={onClick}>
        {body}
      </NavigableLink>
    );
  }

  return (
    <button
      onClick={onClick}
      className='library-card'
      style={{ borderRadius: 10 }}
      onDoubleClick={props.onDoubleClick}
    >
      {body}
    </button>
  );
};

const Card = memo((props: CardShortProps) => {
  const collapsed = useAppSelector((state) => state.ui.libraryCollapsed);

  const onDoubleClick = () => {
    if (props.disablePlayback || !props.uri) return;
    playerService.startPlayback({ context_uri: props.uri });
  };

  if (collapsed) {
    return <CollapsedCard {...props} onDoubleClick={onDoubleClick} />;
  }
  return <CardList {...props} onDoubleClick={onDoubleClick} />;
});

export const ArtistCardShort = ({ artist }: { artist: Artist }) => {
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const composerIds = useComposerIdSet();
  const composer = useAppSelector((state) =>
    state.yourLibrary.myComposers.find((entry) => {
      if (!entry) return false;
      if (entry.spotifyArtistId && entry.spotifyArtistId === artist.id) return true;
      return entry.id === artist.id;
    })
  );

  const isComposer = composerIds.has(artist.id) || !!composer;
  const lifeSpan = composer ? [composer.birthYear, composer.deathYear].filter(Boolean).join('–') : '';

  const subtitleParts = isComposer ? ['Composer'] : ['Artist'];
  if (isComposer && composer?.era) subtitleParts.push(composer.era);
  if (isComposer && lifeSpan) subtitleParts.push(lifeSpan);
  const subtitle = subtitleParts.join(' • ');

  const imageUrl = composer?.imageUrl ?? artist?.images[0]?.url ?? ARTISTS_DEFAULT_IMAGE;

  const preferredCatalogId =
    composer?.id ??
    getCatalogIdForSpotifyId(composer?.spotifyArtistId ?? artist.id ?? null);
  const destination = isComposer
    ? `/composer/${preferredCatalogId ?? composer?.spotifyArtistId ?? artist.id}`
    : `/artist/${artist.id}`;

  return (
    <ArtistActionsWrapper artist={artist} trigger={['contextMenu']}>
      <div>
        <Card
          rounded
          uri={artist.uri}
          linkTo={destination}
          title={highlightText(artist.name ?? '', search)}
          isCurrent={contextUri === artist.uri}
          subtitle={highlightText(subtitle, search)}
          image={imageUrl}
        />
      </div>
    </ArtistActionsWrapper>
  );
};

export const AlbumCardShort = memo(({ album }: { album: Album }) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const albumImage = album?.images?.[0]?.url ?? PLAYLIST_DEFAULT_IMAGE;

  const subtitleParts = ['Album'];
  const artistNames = (album.artists ?? []).map(({ name }) => name).filter(Boolean);
  if (artistNames.length) {
    subtitleParts.push(artistNames.join(', '));
  }

  return (
    <AlbumActionsWrapper album={album} trigger={['contextMenu']}>
      <div>
        <Card
          uri={album.uri}
          linkTo={`/album/${album.id}`}
          beforeNavigate={() => {
            if (!userId) {
              dispatch(uiActions.openLoginModal(albumImage));
              return false;
            }
            return true;
          }}
          title={highlightText(album.name ?? '', search)}
          image={albumImage}
          subtitle={highlightText(subtitleParts.join(' • '), search)}
          isCurrent={contextUri === album.uri}
        />
      </div>
    </AlbumActionsWrapper>
  );
});

const PlaylistCardShort = memo(({ playlist }: { playlist: Playlist }) => {
  const dispatch = useAppDispatch();
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);

  const isMixBuilder = isMixBuilderPlaylist(playlist);
  const destination = isMixBuilder
    ? MIX_BUILDER_ROUTE
    : playlist.id === 'liked-songs'
    ? '/collection/tracks'
    : `/playlist/${playlist.id}`;
  const subtitle = isMixBuilder
    ? 'Tool • Build custom mixes'
    : `Playlist • ${playlist.owner?.display_name ?? 'Unknown'}`;

  const card = (
    <Card
      linkTo={destination}
      uri={playlist.uri}
      title={highlightText(playlist.name ?? '', search)}
      disabled={!playlist.tracks?.total}
      disablePlayback={isMixBuilder}
      isCurrent={isMixBuilder ? false : contextUri === playlist.uri}
      subtitle={highlightText(subtitle, search)}
      image={playlist?.images?.length ? playlist?.images[0]?.url : PLAYLIST_DEFAULT_IMAGE}
    />
  );

  if (isMixBuilder) {
    return <div>{card}</div>;
  }

  return (
    <PlayistActionsWrapper
      playlist={playlist}
      trigger={['contextMenu']}
      onRefresh={() => {
        dispatch(yourLibraryActions.fetchMyPlaylists());
      }}
    >
      <div>{card}</div>
    </PlayistActionsWrapper>
  );
});

const WorkCardShort = memo(({ work }: { work: LibraryWork }) => {
  const search = useAppSelector((state) => state.yourLibrary.search);
  const composerImages = useComposerImageMap();
  const subtitleParts = ['Work'];
  if (work.composerName) subtitleParts.push(work.composerName);
  const catalogueDisplay = work.catalogueNumber ?? work.catalogueNumberNormalized;
  if (catalogueDisplay) subtitleParts.push(catalogueDisplay);
  const subtitle = subtitleParts.join(' • ');
  const artwork = resolveWorkImageUrl(work, composerImages) ?? PLAYLIST_DEFAULT_IMAGE;

  return (
    <div>
      <Card
        uri={work.uri}
        title={highlightText(work.name ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={artwork}
        disabled
        linkTo={`/work/${work.id}`}
      />
    </div>
  );
});

const RecordingCardShort = memo(({ recording }: { recording: LibraryRecording }) => {
  const search = useAppSelector((state) => state.yourLibrary.search);
  const subtitleParts = ['Recording'];
  if (recording.performerName) subtitleParts.push(recording.performerName);
  if (recording.albumName) subtitleParts.push(recording.albumName);
  if (recording.year) subtitleParts.push(recording.year);
  const subtitle = subtitleParts.join(' • ');

  return (
    <div>
      <Card
        uri={recording.uri}
        title={highlightText(recording.workName ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={recording.imageUrl ?? recording.albumImageUrl ?? PLAYLIST_DEFAULT_IMAGE}
        disabled
        linkTo={`/recording/${recording.id}`}
      />
    </div>
  );
});

const ComposerCardShort = memo(({ composer }: { composer: LibraryComposer }) => {
  const search = useAppSelector((state) => state.yourLibrary.search);
  const fallbackImage = useAppSelector((state) => {
    const targetIds = [composer.spotifyArtistId, composer.id].filter(Boolean);
    const match = state.yourLibrary.myArtists.find((entry) => targetIds.includes(entry.id));
    return match?.images?.[0]?.url;
  });

  const subtitleParts = ['Composer'];
  if (composer.era) subtitleParts.push(composer.era);
  if (composer.birthYear || composer.deathYear) {
    const lifeSpan = [composer.birthYear, composer.deathYear].filter(Boolean).join('–');
    if (lifeSpan) {
      subtitleParts.push(lifeSpan);
    }
  }

  const subtitle = subtitleParts.join(' • ');

  return (
    <div>
      <Card
        uri={composer.uri}
        title={highlightText(composer.name ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={composer.imageUrl ?? fallbackImage ?? ARTISTS_DEFAULT_IMAGE}
        rounded
        disabled
        linkTo={`/composer/${
          composer.id ??
          getCatalogIdForSpotifyId(composer.spotifyArtistId ?? composer.id ?? null) ??
          composer.spotifyArtistId ??
          composer.id
        }`}
      />
    </div>
  );
});

export const ListItemComponent = ({
  item,
}: {
  item: Artist | Playlist | Album | LibraryWork | LibraryRecording | LibraryComposer;
}) => {
  const key = `${item.type}-${item.id}`;
  if (item.type === 'artist') return <ArtistCardShort key={key} artist={item} />;
  if (item.type === 'album') return <AlbumCardShort key={key} album={item} />;
  if (item.type === 'composer') return <ComposerCardShort key={key} composer={item} />;
  if (item.type === 'work') return <WorkCardShort key={key} work={item} />;
  if (item.type === 'recording') return <RecordingCardShort key={key} recording={item} />;
  return <PlaylistCardShort key={key} playlist={item} />;
};
